import { useState } from "react";
import { toast } from "sonner";
import { FolderPlus, Images } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { AlbumItem, useConfirmDelete } from "./AlbumItem";

const onApiError = (err) => toast.error(formatApiError(err.response?.data?.detail));

export default function AlbumPanel({ albums, selectedId, onSelect, refresh }) {
  const [newName, setNewName] = useState("");
  const [creating, setCreating] = useState(false);
  const [renamingId, setRenamingId] = useState(null);
  const del = useConfirmDelete();

  const createAlbum = async (e) => {
    e.preventDefault();
    if (!newName.trim()) return;
    setCreating(true);
    try {
      const { data } = await api.post("/albums", { name: newName.trim() });
      toast.success(`Album “${data.name}” created`);
      setNewName("");
      await refresh();
      onSelect(data.id);
    } catch (err) {
      onApiError(err);
    } finally {
      setCreating(false);
    }
  };

  const renameAlbum = async (id, name) => {
    if (!name.trim()) return setRenamingId(null);
    try {
      await api.patch(`/albums/${id}`, { name: name.trim() });
      toast.success("Album renamed");
      setRenamingId(null);
      await refresh();
    } catch (err) {
      onApiError(err);
    }
  };

  const deleteAlbum = async (album) => {
    try {
      await api.delete(`/albums/${album.id}`);
      toast.success(`Album “${album.name}” deleted`);
      del.clear();
      await refresh();
    } catch (err) {
      onApiError(err);
    }
  };

  return (
    <aside className="h-fit rounded-3xl border border-gold/40 bg-white/85 p-5 shadow-sm backdrop-blur" data-testid="album-panel">
      <h2 className="flex items-center gap-2 font-serif text-xl font-semibold text-burgundy">
        <Images className="h-5 w-5 text-gold" /> Albums
      </h2>

      <form onSubmit={createAlbum} className="mt-4 flex gap-2">
        <input
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          placeholder="New album name…"
          data-testid="album-create-input"
          className="min-w-0 flex-1 rounded-xl border border-gold/40 bg-white px-3 py-2 text-sm outline-none focus:border-gold focus:ring-2 focus:ring-gold/25"
        />
        <button
          type="submit"
          disabled={creating || !newName.trim()}
          data-testid="album-create-button"
          className="shrink-0 rounded-xl border border-gold bg-gold px-3 py-2 text-white transition-all hover:bg-golddeep disabled:opacity-50"
          aria-label="Create album"
        >
          <FolderPlus className="h-4 w-4" />
        </button>
      </form>

      <ul className="mt-5 space-y-2" data-testid="album-list">
        {albums.length === 0 && (
          <li className="rounded-xl border border-dashed border-gold/50 px-4 py-6 text-center text-sm text-plum/60">
            No albums yet — create your first above.
          </li>
        )}
        {albums.map((a) => (
          <AlbumItem
            key={a.id}
            album={a}
            selected={selectedId === a.id}
            renaming={renamingId === a.id}
            confirmDelete={del.confirmId === a.id}
            onSelect={() => onSelect(a.id)}
            onStartRename={() => setRenamingId(a.id)}
            onCancelRename={() => setRenamingId(null)}
            onRename={(name) => renameAlbum(a.id, name)}
            onAskDelete={() => del.ask(a.id)}
            onDelete={() => deleteAlbum(a)}
          />
        ))}
      </ul>
    </aside>
  );
}
